import { useMemo } from "react"

import { useFetchGuests } from "src/data/guests/queries/guestQueries"
import {
  dateToUTCISOStringWithoutTzConversion,
  utcToHomeTimezone,
} from "src/utils/l10n"

export function useParadiseGuestPlotBands({
  homeId,
  dateRange,
  useHomeTimezone,
  homeTimezone,
  enabled = true,
}: {
  homeId: string
  dateRange: { startAt: Date; endAt: Date }
  useHomeTimezone: boolean
  homeTimezone: string
  enabled?: boolean
}) {
  const fetchGuests = useFetchGuests(homeId, {
    checkin_filter: {
      to: dateRange.endAt,
    },
    checkout_filter: {
      from: dateRange.startAt,
    },
    options: {
      enabled,
    },
  })

  const firstDataPoint = useHomeTimezone
    ? convertUtcToHomeTimezone(dateRange.startAt, homeTimezone)
    : dateRange.startAt
  const lastDataPoint = useHomeTimezone
    ? convertUtcToHomeTimezone(dateRange.endAt, homeTimezone)
    : dateRange.endAt

  const guests = fetchGuests.data?.guests

  const guestPlotbands = useMemo(() => {
    if (!guests || !firstDataPoint || !lastDataPoint || !enabled)
      return undefined

    return guests
      .map((guest) => {
        const { checkin_time, checkout_time } = guest
        if (!checkin_time || !checkout_time) return undefined

        const checkinTime = useHomeTimezone
          ? convertUtcToHomeTimezone(checkin_time, homeTimezone)
          : checkin_time
        const checkoutTime = useHomeTimezone
          ? convertUtcToHomeTimezone(checkout_time, homeTimezone)
          : checkout_time

        if (!checkinTime || !checkoutTime) return undefined

        return {
          color: "rgba(145, 141, 141, 0.3)",
          from: Math.max(
            new Date(checkinTime).getTime(),
            new Date(firstDataPoint).getTime()
          ),
          to: Math.min(
            new Date(checkoutTime).getTime(),
            new Date(lastDataPoint).getTime()
          ),
          label: {
            text: guest.name,
            style: {
              color: "#666",
              fontWeight: "bold",
            },
          },
        }
      })
      .filter((d): d is NonNullable<typeof d> => d !== undefined)
  }, [
    guests,
    firstDataPoint,
    lastDataPoint,
    enabled,
    useHomeTimezone,
    homeTimezone,
  ])

  return guestPlotbands
}

function convertUtcToHomeTimezone(
  d: Date | string | undefined,
  timezone?: string
): string | undefined {
  if (!d) {
    return undefined
  }
  return dateToUTCISOStringWithoutTzConversion(utcToHomeTimezone(d, timezone))
}
